// ─────────────────────────────────────────────────────────────────────────────
// AUDITORÍA — QUÉ SE GUARDA DE CADA ACCIÓN
//
// Hasta ahora cada evento era una sola frase suelta ("Camión editado: MACK
// VISION"). Servía para ver que algo pasó, no para auditar: no decía quién lo
// hizo ni qué cambió exactamente.
//
// Ahora cada evento lleva, además de la frase:
//   · quién  — nombre y id del usuario de la sesión
//   · qué    — la acción (crear, editar, archivar…) y la entidad
//   · cuál   — el registro concreto al que afectó
//   · cómo   — la lista de campos que cambiaron, con su antes y su después
//
// La frase se conserva tal cual porque es lo que hay guardado en los eventos
// viejos, y porque es lo que se busca al escribir en el buscador.
// ─────────────────────────────────────────────────────────────────────────────

import type { AuditEvent, AccionAuditoria, CambioAuditoria } from './types';

// ── ACCIONES ────────────────────────────────────────────────────────────────
// El `id` es lo que se guarda en la columna `accion`. `prueba` solo se usa
// para los eventos viejos, que no tienen esa columna y hay que deducirlos de
// la frase.
//
// El orden importa: "desarchivado" tiene que probarse antes que "archivado",
// y "anulada" antes que el resto.
export const ACCIONES: {
  id: AccionAuditoria;
  label: string;
  prueba: RegExp;
}[] = [{
  id: 'entrar',
  label: 'Inicio de sesión',
  // "inicio de sesión", no "inició sesión": la frase que escribe la app lleva
  // el "de" en medio.
  prueba: /inicio de sesi|inici. sesi.n|entr. al sistema/i
}, {
  id: 'salir',
  label: 'Cierre de sesión',
  prueba: /cierre de sesi|cerr. sesi.n|sali. del sistema/i
}, {
  id: 'autorizar',
  label: 'Autorización',
  prueba: /autorizad/i
}, {
  id: 'anular',
  label: 'Anulación',
  prueba: /anulad/i
}, {
  id: 'eliminar',
  label: 'Eliminación',
  prueba: /eliminad|borrad/i
}, {
  id: 'desarchivar',
  label: 'Devuelto al listado',
  prueba: /desarchivad|devuelt/i
}, {
  id: 'archivar',
  label: 'Archivo',
  prueba: /archivad/i
}, {
  id: 'crear',
  label: 'Creación',
  prueba: /cread[oa]|registrad[oa]/i
}, {
  id: 'editar',
  label: 'Edición',
  prueba: /editad[oa]|actualizad[oa]|cambiada/i
}, {
  id: 'imprimir',
  label: 'Impresión',
  prueba: /impres[oa]/i
}, {
  id: 'correo',
  label: 'Aviso por correo',
  prueba: /correo (enviado|no se pudo)/i
}, {
  id: 'exportar',
  label: 'Exportación',
  prueba: /exportad[oa]|generad[oa] en/i
}, {
  id: 'resolver',
  label: 'Resolución',
  prueba: /resuelt[oa]/i
}];
export const ETIQUETA_ACCION: Record<AccionAuditoria, string> = {
  crear: 'Creación',
  editar: 'Edición',
  eliminar: 'Eliminación',
  archivar: 'Archivo',
  desarchivar: 'Devuelto al listado',
  autorizar: 'Autorización',
  anular: 'Anulación',
  imprimir: 'Impresión',
  exportar: 'Exportación',
  correo: 'Aviso por correo',
  resolver: 'Resolución',
  entrar: 'Inicio de sesión',
  salir: 'Cierre de sesión',
  otro: 'Actividad'
};

// Deducción para los eventos anteriores a este cambio, que solo tienen frase.
export function accionDe(mensaje: string): AccionAuditoria {
  return ACCIONES.find(a => a.prueba.test(mensaje))?.id ?? 'otro';
}

// La entidad de un evento viejo es la primera palabra de la frase
// ("Usuario creado: Juan"), salvo las de varias palabras ya conocidas.
const ENTIDADES_COMPUESTAS = ['Orden de pedido', 'Inicio de sesión', 'Cierre de sesión', 'Reporte de consumos'];
export function entidadDe(mensaje: string): string {
  const hallada = ENTIDADES_COMPUESTAS.find(e => mensaje.toLowerCase().startsWith(e.toLowerCase()));
  if (hallada) return hallada;
  return mensaje.split(/[\s:]/)[0] || '—';
}

// ── CÓMO SE ESCRIBE UN VALOR EN EL DETALLE ──────────────────────────────────
// Todo se guarda ya convertido a texto: el detalle se lee mucho después, y un
// número o una fecha con el formato del momento vale más que el dato crudo.
export function valorTexto(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (Array.isArray(v)) return v.length ? v.map(valorTexto).join(', ') : '—';
  if (typeof v === 'number') return new Intl.NumberFormat('es-DO').format(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Descripción de un campo auditable: cómo se llama para el que lee el registro
// y, si hace falta, cómo se escribe su valor.
export interface CampoAuditable {
  etiqueta: string;
  formato?: (v: unknown) => string;
}
export type Campos = Record<string, CampoAuditable | string>;
const normalizarCampo = (c: CampoAuditable | string): CampoAuditable => typeof c === 'string' ? {
  etiqueta: c
} : c;

// ── EL DIFERENCIAL ──────────────────────────────────────────────────────────
// `campos` es una lista explícita, no las claves del objeto: así el registro
// no se llena de ids internos, banderas de control ni campos que no significan
// nada para quien audita. Lo que no esté en la lista, no se registra.
//
// Con `antes` en null (un alta) devuelve los campos con los que se creó, para
// que el detalle también responda "¿qué creó?" y no solo "¿qué editó?".
// Recibe `object` y no `Record<string, unknown>` porque lo normal es pasarle
// una interfaz declarada (Sucursal, Equipo…), y esas no traen índice de
// cadena; exigirlo obligaría a convertir en cada llamada.
export function diferencias(antesObj: object | null | undefined, despuesObj: object, campos: Campos): CambioAuditoria[] {
  const antes = antesObj as Record<string, unknown> | null | undefined;
  const despues = despuesObj as Record<string, unknown>;
  const salida: CambioAuditoria[] = [];
  for (const clave of Object.keys(campos)) {
    const def = normalizarCampo(campos[clave]);
    const escribir = def.formato ?? valorTexto;
    const nuevo = escribir(despues[clave]);
    if (!antes) {
      // En un alta no hay con qué comparar: se apunta con qué nació, y se
      // omite lo que quedó vacío para que el detalle no sea una lista de
      // rayas.
      if (nuevo !== '—') salida.push({
        campo: def.etiqueta,
        antes: '',
        despues: nuevo
      });
      continue;
    }
    const viejo = escribir(antes[clave]);
    if (viejo !== nuevo) salida.push({
      campo: def.etiqueta,
      antes: viejo,
      despues: nuevo
    });
  }
  return salida;
}

// ── LECTURA DE LA TABLA ─────────────────────────────────────────────────────
// Una fila de `auditoria` puede venir de tres épocas:
//   1. la de ahora, con todas las columnas;
//   2. la anterior, solo con `mensaje` = "27 jul, 02:31 p. m. - Camión creado: X";
//   3. la de un proyecto al que todavía no se le corrió el script SQL, donde
//      las columnas nuevas llegan como undefined.
// Las tres tienen que verse bien, así que todo se deduce de la frase cuando
// falta la columna.
export function normalizarEvento(entrada: Record<string, unknown> | string): AuditEvent {
  // La caché de versiones anteriores guardaba cada evento como una cadena
  // suelta, sin fila alrededor.
  const fila: Record<string, unknown> = typeof entrada === 'string' ? {
    mensaje: entrada
  } : entrada;
  const completo = String(fila.mensaje ?? '');
  const corte = completo.indexOf(' - ');
  const sello = corte === -1 ? '' : completo.slice(0, corte).trim();
  const mensaje = corte === -1 ? completo : completo.slice(corte + 3).trim();
  const accion = fila.accion as AccionAuditoria || accionDe(mensaje);
  const cambios = Array.isArray(fila.cambios) ? fila.cambios as CambioAuditoria[] : [];
  return {
    id: String(fila.id ?? completo),
    sello,
    fechaISO: String(fila.fecha ?? ''),
    usuario: String(fila.usuario ?? ''),
    usuarioId: String(fila.usuario_id ?? ''),
    accion,
    entidad: String(fila.entidad ?? '') || entidadDe(mensaje),
    registro: String(fila.registro ?? ''),
    mensaje,
    completo,
    cambios
  };
}

// El día es lo que va antes de la coma del sello ("27 jul, 02:31 p. m.").
export const diaDe = (sello: string) => {
  const coma = sello.indexOf(',');
  return coma === -1 ? sello : sello.slice(0, coma).trim();
};
export const horaDe = (sello: string) => {
  const coma = sello.indexOf(',');
  return coma === -1 ? '' : sello.slice(coma + 1).trim();
};